import { useEffect, useRef } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuth } from '@/lib/hooks/useAuth';
import * as notificationsService from '@/lib/services/notifications';
import type { UserNotification } from '@/lib/types/notifications.types';

export const userNotificationKeys = {
  all: ['userNotifications'] as const,
  inboxStateRoot: () => [...userNotificationKeys.all, 'inboxState'] as const,
  inboxState: (userId: string) =>
    [...userNotificationKeys.inboxStateRoot(), userId] as const,
  lists: () => [...userNotificationKeys.all, 'list'] as const,
  list: (userId: string, pageSize: number) =>
    [...userNotificationKeys.lists(), userId, { pageSize }] as const,
  details: () => [...userNotificationKeys.all, 'detail'] as const,
  detail: (userId: string, notificationId: string) =>
    [...userNotificationKeys.details(), userId, notificationId] as const,
  unreadCountRoot: () => [...userNotificationKeys.all, 'unreadCount'] as const,
  unreadCount: (userId: string) =>
    [...userNotificationKeys.unreadCountRoot(), userId] as const,
  destination: (userId: string, notificationId: string) =>
    [
      ...userNotificationKeys.all,
      'destination',
      userId,
      notificationId,
    ] as const,
  destinationMap: (userId: string, notificationIds: string[]) =>
    [
      ...userNotificationKeys.all,
      'destinationMap',
      userId,
      notificationIds,
    ] as const,
};

export function useInfiniteUserNotifications(pageSize: number = 25) {
  const { user, isAuthenticated } = useAuth();

  return useInfiniteQuery({
    queryKey: userNotificationKeys.list(user?.id || '', pageSize),
    queryFn: ({ pageParam }) =>
      notificationsService.getUserNotifications(
        user?.id,
        pageParam as number,
        pageSize
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: isAuthenticated,
  });
}

export function useUserNotification(notificationId: string) {
  const { user, isAuthenticated } = useAuth();

  return useQuery({
    queryKey: userNotificationKeys.detail(user?.id || '', notificationId),
    queryFn: () =>
      notificationsService.getUserNotificationById(user?.id, notificationId),
    enabled: isAuthenticated && !!notificationId,
  });
}

export function useUnreadNotificationCount() {
  const { user, isAuthenticated } = useAuth();

  return useQuery({
    queryKey: userNotificationKeys.unreadCount(user?.id || ''),
    queryFn: () => notificationsService.getUnreadNotificationCount(user?.id),
    enabled: isAuthenticated,
    staleTime: 30 * 1000,
  });
}

export function useNotificationInboxState() {
  const { user, isAuthenticated } = useAuth();

  return useQuery({
    queryKey: userNotificationKeys.inboxState(user?.id || ''),
    queryFn: () => notificationsService.getNotificationInboxState(user?.id),
    enabled: isAuthenticated,
    staleTime: 30 * 1000,
  });
}

export function useMarkNotificationRead() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) =>
      notificationsService.markUserNotificationRead(user?.id, notificationId),
    onSuccess: (_, notificationId) => {
      queryClient.invalidateQueries({ queryKey: userNotificationKeys.all });
      queryClient.invalidateQueries({
        queryKey: userNotificationKeys.detail(user?.id || '', notificationId),
      });
    },
  });
}

export function useMarkNotificationsReadByDeliveryKey() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deliveryKey: string) =>
      notificationsService.markNotificationsReadByDeliveryKey(
        user?.id,
        deliveryKey
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userNotificationKeys.all });
    },
  });
}

export function useMarkNotificationsInboxSeen() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsService.markNotificationsInboxSeen(user?.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: userNotificationKeys.inboxStateRoot(),
      });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notificationsService.markAllUserNotificationsRead(user?.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userNotificationKeys.all });
    },
  });
}

export function useNotificationDestinationAvailability(
  notification: UserNotification | null | undefined
) {
  const { user, isAuthenticated } = useAuth();

  return useQuery({
    queryKey: userNotificationKeys.destination(
      user?.id || '',
      notification?.id ?? ''
    ),
    queryFn: () =>
      notificationsService.isNotificationDestinationAvailable(
        user?.id,
        notification!
      ),
    enabled: isAuthenticated && !!notification,
  });
}

export function useNotificationDestinationAvailabilityMap(
  notifications: UserNotification[]
) {
  const { user, isAuthenticated } = useAuth();
  const notificationIds = notifications.map((notification) => notification.id);

  return useQuery({
    queryKey: userNotificationKeys.destinationMap(
      user?.id || '',
      notificationIds
    ),
    queryFn: () =>
      notificationsService.getNotificationDestinationAvailabilityMap(
        user?.id,
        notifications
      ),
    enabled: isAuthenticated && notifications.length > 0,
    staleTime: 30 * 1000,
  });
}

export function useNotificationOpenTracking(
  notificationId: string | undefined,
  clearNotificationParam: () => void
) {
  const mutation = useMarkNotificationsReadByDeliveryKey();
  const handledNotificationRef = useRef<string | null>(null);

  useEffect(() => {
    if (!notificationId || handledNotificationRef.current === notificationId) {
      return;
    }

    handledNotificationRef.current = notificationId;

    void mutation
      .mutateAsync(notificationId)
      .catch((error) => {
        console.error('Failed to track notification open:', error);
      })
      .finally(() => {
        clearNotificationParam();
      });
  }, [clearNotificationParam, mutation, notificationId]);
}
